import { datadogRum } from '@datadog/browser-rum';
import type { LDEvaluationDetail } from 'launchdarkly-react-client-sdk';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import type { ComponentProps, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import settings from '@/constants/constants';
import { getAppEnv } from '@/constants/env';
import ffDefaults from '@/constants/ffDefaults';
import { FFLoadingProvider } from '@/context/featureFlags/FFLoadingContext';
import { Logger } from '@montugroup/utilities';

import { isMobile } from '@/utils/mobile';

const logger = new Logger('FeatureFlagProvider');

const LAUNCH_DARKLY_API_KEY = settings.launchDarklyConfig?.clientSideID;

/**
 * Main feature flag provider that combines LaunchDarkly client with loading state management.
 *
 * **Key Features:**
 * - Prevents excessive identify calls in development via shared-anonymous context
 * - Provides immediate bootstrap values while actual flags load
 * - Tracks loading state so components know when actual flags are ready
 *
 * @param children - React components that need feature flags
 * @param props - Additional LDProvider props
 */
export function FeatureFlagProvider({
  children,
  ...props
}: PropsWithChildren<Partial<ComponentProps<typeof LDProvider>>>) {
  // Use environment-based logic instead of flag evaluation
  // Development: shared-anonymous to prevent excessive context creation
  // Production: unique anonymous contexts for proper A/B testing
  const isNotProductionEnv = getAppEnv() !== 'production';

  const initialContext = {
    kind: 'user',
    anonymous: true,
    ...(isNotProductionEnv && { key: 'shared-anonymous' }),
    isMobile: isMobile(),
  };

  useEffect(() => {
    logger.info('LD client initial identity', isNotProductionEnv ? 'shared-anonymous' : 'unique-anonymous');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LDProvider
      clientSideID={LAUNCH_DARKLY_API_KEY}
      options={{
        bootstrap: { ...ffDefaults },
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDEvaluationDetail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      }}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
      context={initialContext}
      deferInitialization={false}
      {...props}
    >
      <FFLoadingProvider>{children}</FFLoadingProvider>
    </LDProvider>
  );
}
